import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import type { CellDataCustom } from '../../common/utils/table';
import { getDefaultRowValue } from '../../common/utils/table';
import { ComplexRowInputHandler } from './forms/ComplexRowInputHandler';
import type {
  EditTermsSchema,
  ProductFeeTermSchema,
  ProductFeeTermType,
  TAccountProduct
} from '../../common';
import {
  createMutationPromise,
  CustomFeesSchema,
  errorToMessageString,
  ProductSchema,
  RefinedTermSchema,
  termToDurationString
} from '../../common';
import { mutateProductFees, useAccountStore } from '../../contexts';
import {
  ButtonContainer,
  ErrorWrapper,
  HelpTooltip,
  InputWrapper,
  PrimaryButton,
  PrimaryIconButton,
  SecondaryButton,
  TransparentIconButton
} from '../atoms';
import type { TTableHead } from './ComplexTable';
import { ComplexTableHeadCell, TableCell } from './ComplexTable';
import { cloneDeep, first, isEmpty, last, sortBy, uniq } from 'lodash';
import { ActionsWrapper, EditableTable } from '../organisms/EditableTable';
import get from 'lodash/get';
import { massageFeeAmounts, massageTermRanges } from '../../common/utils/fees';
import isEqual from 'lodash/isEqual';
import { FEE_IDS } from '../types';
import { useAutoAnimate } from '@formkit/auto-animate/react';

const ACTIONS_CELL_WIDTH = '105px';
const FIRST_CELL_WIDTH = '190px';
const TERMS = 'terms';
const CUSTOM_FEES = 'customFees';
const FEES = 'fees';

type Props = {
  product: TAccountProduct;
  merchantId: string;
  // columns: ReadonlyArray<ReadonlyArray<AccountProductBlockRow>>;
  itemTitle?: string;
  itemSubtitle?: string;
  feeId?: FEE_IDS;
  onUpdate?: (terms: EditTermsSchema['terms']) => Promise<unknown>;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const commonFields = ProductSchema.pick({
  fees: true
}).augment({
  terms: RefinedTermSchema,
  customFees: CustomFeesSchema
});

type EditFormSchema = typeof commonFields;
type EditFormData = z.infer<typeof commonFields>;

const InputHeaderWrapper = styled.span(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.margins.xs};
    margin-top: ${theme.margins.xs};

    > span {
      flex-grow: 0;
      flex-shrink: 0;
      margin-top: -4px;
    }

    > div {
      flex-grow: 1;
      flex-basis: 0;
      margin-bottom: 0;

      label {
        margin-top: -25px;
        margin-bottom: -2px;
      }

      input {
        padding: 0.25rem;
        font-size: ${theme.fonts.sizes.h6};
      }
    }
  `
);

const FlexHeadWrapper = styled.span<TTableHead>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    > span:nth-child(2) {
      margin-right: -15px;
    }
  `
);

export const AccountFeeMatrix = ({
  product,
  merchantId,
  itemSubtitle = 'Account Servicing Fees',
  feeId = FEE_IDS.AccountServicePlannedFee
}: Props) => {
  const [parent] = useAutoAnimate();
  const [isEditing, setIsEditing] = useState(false);
  const [rollbackData, setRollbackData] = useState<EditFormData['terms']>();
  const availableTerms = uniq(sortBy([...get(product, 'availableTerms', [])]));
  const defaultAmount = get(product, `fees.${feeId}.amount`, 0);
  const defaultLoanAmounts: ProductFeeTermType['loanAmounts'] = [
    {
      min: product.minLoanAmount,
      max: product.maxLoanAmount,
      amount: defaultAmount
    }
  ];
  const defaultTerms = availableTerms.map((t, i) => ({
    min: get(availableTerms, i - 1, 0),
    max: t,
    unit: 'month',
    amount: defaultAmount,
    loanAmounts: defaultLoanAmounts
  }));
  let _terms: Array<ProductFeeTermType> = get(
    product,
    `fees.${feeId}.conditions.terms`,
    []
  );
  const isUsingDefaultTerms = !_terms.length;
  _terms = !_terms?.length ? defaultTerms : _terms;
  const { loanAmounts = defaultLoanAmounts } = _terms[0] || {};

  const getCellsData = (): Array<Array<CellDataCustom>> => {
    return loanAmounts.map((amount, index) => {
      return [
        {
          contentType: 'range' as const,
          step: 0.01,
          content: {
            min: amount.min,
            max: amount.max
          }
        },
        ...Array(_terms.length)
          .fill(undefined)
          .flatMap((_, secondIndex) => {
            const termDefaultAmount = get(product, `fees.${feeId}.amount`, 0);
            const weeklyAmount = get(
              product,
              `fees.${feeId}.conditions.terms[${secondIndex}].loanAmounts[${index}].frequencies`,
              []
            ).find((f) => f.frequency.toLowerCase() === 'weekly')?.amount;
            const fortnightlyAmount = get(
              product,
              `fees.${feeId}.conditions.terms[${secondIndex}].loanAmounts[${index}].frequencies`,
              []
            ).find((f) => f.frequency.toLowerCase() === 'fortnightly')?.amount;
            const monthlyAmount = get(
              product,
              `fees.${feeId}.conditions.terms[${secondIndex}].loanAmounts[${index}].frequencies`,
              []
            ).find((f) => f.frequency.toLowerCase() === 'monthly')?.amount;

            return [
              {
                contentType: 'number' as const,
                content: weeklyAmount || termDefaultAmount
              },
              {
                contentType: 'number' as const,
                content: fortnightlyAmount || termDefaultAmount
              },
              {
                contentType: 'number' as const,
                content: monthlyAmount || termDefaultAmount
              }
            ];
          })
      ];
    });
  };

  const zResolver = zodResolver(commonFields);
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    setValue,
    control,
    formState: { errors, isDirty, isSubmitting, isSubmitted }
  } = useForm<EditFormSchema>({
    resolver: function (
      { terms: rawTerms = [], customFees: rawCustomFees = [], ...rest },
      ...args
    ) {
      const data: EditFormData = {
        ...rest,
        customFees: massageFeeAmounts(rawCustomFees),
        // massage overlapping terms
        terms: massageTermRanges(rawTerms)
      };
      // reflect massaged changes live in UI
      if (!isEqual(data.terms, rawTerms)) {
        setValue(TERMS, data.terms);
      }

      if (!isEqual(data.customFees, rawCustomFees)) {
        setValue(CUSTOM_FEES, data.customFees);
      }

      // let zod do it's thing
      return zResolver(data, ...args);
    },
    mode: 'onBlur', // revalidate onBlur
    defaultValues: {
      [FEES]: product[FEES],
      terms: massageTermRanges(_terms),
      customFees: massageFeeAmounts(getCellsData())
    }
  });

  const showSaveButtons = isDirty || (!isSubmitted && isUsingDefaultTerms);

  const updateProductFees = useAccountStore((s) => s.updateProductFees);
  const updateProductFeesMutation = mutateProductFees({
    productId: product.id,
    merchantId,
    updateProductFees,
    mutate: useMutation
  });

  const onSubmit: SubmitHandler<EditFormSchema> = async (data) => {
    const fees: TAccountProduct['fees'] = {
      ...product.fees,
      [feeId]: {
        ...product.fees[feeId],
        conditions: {
          ...product.fees[feeId].conditions,
          terms: data.terms.map((term, i) => ({
            ...term,
            loanAmounts: data.customFees.map((cf, secondIndex) => ({
              ...term.loanAmounts[secondIndex],
              min: (cf[0].content as { min: number; max: number }).min,
              max: (cf[0].content as { min: number; max: number }).max,
              frequencies: [
                {
                  amount: Number(cf.slice(1)[i * 3].content),
                  frequency: 'Weekly'
                },
                {
                  amount: Number(cf.slice(1)[i * 3 + 1].content),
                  frequency: 'Fortnightly'
                },
                {
                  amount: Number(cf.slice(1)[i * 3 + 2].content),
                  frequency: 'Monthly'
                }
              ],
              amount: Number(cf.slice(1)[i * 3].content)
            }))
          }))
        }
      }
    };

    try {
      const productFeesChanged = !isEqual(fees, product.fees);

      if (productFeesChanged) {
        await createMutationPromise(updateProductFeesMutation, fees);
        toast.success(`Successfully Updated ${itemSubtitle}`);
      }
    } catch (err) {
      toast.error(errorToMessageString(err));
    }
  };

  const handleReset = () => {
    reset();
  };

  const onSubmitError: SubmitErrorHandler<EditFormSchema> = (err) => {
    console.error(err);
    toast.error('Something went wrong while trying to submit the form.');
  };

  const { append, remove } = useFieldArray({
    control,
    name: TERMS
  });

  const { update } = useFieldArray({
    control,
    name: CUSTOM_FEES
  });

  const onDelete = (term: z.infer<typeof ProductFeeTermSchema>) => {
    const terms = getValues(TERMS);
    const index = terms.findIndex(
      (t) => t.max === term.max && t.min === term.min
    );

    if (index !== -1) {
      remove(index);
      const customFees = getValues(CUSTOM_FEES);
      customFees.forEach((cf, i) => {
        update(i, [
          ...cf.slice(0, index + 1),
          ...cf.slice(index + 1 + 3) // remove three elements
        ]);
      });
    }
  };

  const onAddColumn = () => {
    const terms = getValues(TERMS);
    const highestRange = Math.max(
      ...terms.map((t) => t.max),
      ...terms.map((t) => t.min),
      0
    );
    const min = Math.ceil(highestRange);

    // update terms
    append({
      min,
      max: min + 1,
      amount: (last(getValues(TERMS)) || { amount: 0 }).amount,
      unit: 'month',
      gst: 0,
      loanAmounts: []
    });

    // update cells
    getValues(CUSTOM_FEES).forEach((_field, index) => {
      const field = Object.values(
        omit(_field, ['id', 'added'])
      ) as Array<CellDataCustom>;

      update(index, [
        ...field,
        ...(field.length > 1
          ? field.slice(-3).map((f) => cloneDeep(f))
          : [
              {
                contentType: 'number' as const,
                content: get(product, `fees.${feeId}.amount`, 0)
              },
              {
                contentType: 'number' as const,
                content: get(product, `fees.${feeId}.amount`, 0)
              },
              {
                contentType: 'number' as const,
                content: get(product, `fees.${feeId}.amount`, 0)
              }
            ])
      ]);
    });
  };

  const ActionsContent = () => {
    return (
      <>
        <ActionsWrapper>
          {isEditing ? (
            <>
              <TransparentIconButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  stopEditingHeaders({ isSaving: false });
                }}
              >
                <span className="material-symbols-rounded">close</span>
              </TransparentIconButton>
              <TransparentIconButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  stopEditingHeaders({ isSaving: true });
                }}
              >
                <span className="material-symbols-rounded">done</span>
              </TransparentIconButton>
            </>
          ) : (
            <>
              <TransparentIconButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  editHeaders();
                }}
              >
                <span className="material-symbols-rounded">edit</span>
              </TransparentIconButton>
            </>
          )}
        </ActionsWrapper>
      </>
    );
  };

  const stopEditingHeaders = ({
    isSaving = false
  }: { isSaving?: boolean } = {}) => {
    if (!isSaving && rollbackData) {
      setValue(TERMS, rollbackData);
    }

    setRollbackData(undefined);
    setIsEditing(false);
  };

  const editHeaders = () => {
    setRollbackData(getValues(TERMS));
    setIsEditing(true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onSubmitError)}>
      <ComplexRowInputHandler
        key={`${first(getValues(CUSTOM_FEES))?.length || 0}-${
          getValues(TERMS).length || 0
        }`}
        row={{
          name: CUSTOM_FEES,
          title: '',
          content: [],
          contentType: 'table',
          nonEditable: false,
          tableConfig: {
            getValues,
            title: itemSubtitle,
            getDefaultValue: getDefaultRowValue(product)
          }
        }}
        control={control}
        errors={errors}
        register={register}
      >
        <EditableTable.HeaderActions>
          <PrimaryIconButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddColumn();
            }}
          >
            <span>Add Column</span>
            <span className="material-symbols-rounded">add</span>
          </PrimaryIconButton>
        </EditableTable.HeaderActions>
        <EditableTable.TableHeader>
          <ComplexTableHeadCell
            verticalAlign={'bottom'}
            style={{
              minWidth: FIRST_CELL_WIDTH,
              maxWidth: FIRST_CELL_WIDTH,
              width: FIRST_CELL_WIDTH
            }}
          >
            <FlexHeadWrapper>
              <span>
                <span>Loan Amount</span>
                <HelpTooltip
                  text={
                    'A Loan Amount range with a maximum of $-1.00 equates to any number greater than the minimum.'
                  }
                />
              </span>
            </FlexHeadWrapper>
          </ComplexTableHeadCell>
          {getValues(TERMS).map((t, i) => (
            <ComplexTableHeadCell
              colSpan={3}
              verticalAlign={'bottom'}
              key={uuidv4()}
              isEditing={isEditing}
            >
              <FlexHeadWrapper>
                {isEditing ? (
                  <span>
                    <InputHeaderWrapper>
                      <InputWrapper
                        name={`terms.${i}.min`}
                        type="number"
                        label="Min"
                        required
                        step={0.01}
                        register={register}
                        hideErrorMessage
                        errorMessage={get(
                          errors,
                          `terms[${i}].min.message`,
                          ''
                        )}
                      />
                      <span>-</span>
                      <InputWrapper
                        name={`terms.${i}.max`}
                        type="number"
                        label="Max"
                        step={0.01}
                        hideErrorMessage
                        required
                        register={register}
                        errorMessage={get(
                          errors,
                          `terms[${i}].max.message`,
                          ''
                        )}
                      />
                    </InputHeaderWrapper>
                  </span>
                ) : (
                  <>
                    <span>{termToDurationString(t)}</span>
                    <span>
                      <TransparentIconButton
                        type="button"
                        larger
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(t);
                        }}
                      >
                        <span className="material-symbols-rounded">delete</span>
                      </TransparentIconButton>
                    </span>
                  </>
                )}
              </FlexHeadWrapper>
            </ComplexTableHeadCell>
          ))}
          <ComplexTableHeadCell
            verticalAlign={'bottom'}
            style={{
              height: '58px',
              minWidth: ACTIONS_CELL_WIDTH,
              maxWidth: ACTIONS_CELL_WIDTH,
              width: ACTIONS_CELL_WIDTH
            }}
          >
            <FlexHeadWrapper>
              <ActionsContent />
            </FlexHeadWrapper>
          </ComplexTableHeadCell>
        </EditableTable.TableHeader>
        <EditableTable.PreBodyRow>
          <TableCell>
            <span></span>
          </TableCell>
          {getValues(TERMS).map((t, i) => (
            <>
              <TableCell key={`${i}-1`}>
                <span title={'Weekly'}>W</span>
              </TableCell>
              <TableCell key={`${i}-2`}>
                <span title={'Fortnightly'}>F</span>
              </TableCell>
              <TableCell key={`${i}-3`}>
                <span title={'Monthly'}>M</span>
              </TableCell>
            </>
          ))}
          <TableCell>
            <span></span>
          </TableCell>
        </EditableTable.PreBodyRow>
      </ComplexRowInputHandler>
      {!isEmpty(errors) && (
        <ErrorWrapper>{errorToMessageString(errors)}</ErrorWrapper>
      )}
      <div ref={parent}>
        {showSaveButtons && (
          <FormFooter
            horizontalSpacing="10px"
            verticalSpacing="0px"
            justify="end"
          >
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
            >
              <span>Cancel</span>
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              <span>Save Fee</span>
            </PrimaryButton>
          </FormFooter>
        )}
      </div>
    </form>
  );
};
